// src/oura/merchant/drawers/QuestionSheet.tsx
//
// One question we asked the merchant, in the one merchant drawer
// (MerchantDrawers.tsx). The title is the question itself, whole. What the
// sheet holds depends on where the question stands (specs v1.1, A-H):
//
//   1 Needs your reply, not answered  "Your answer" with the answer box.
//                                     Send answer, or Not now (Later).
//   2 Needs your reply, we replied    "Reply to {AM}": our words since theirs,
//                                     then a reply box. Send reply.
//   3 Later                           Answer now reopens it here, in place.
//   4 Being revised / 5 Answered      "What happens now". Change my answer,
//                                     after a confirm, reopens it.
//
// The status line under the title is the row's group word and its one fact
// (a date; late in rose). Everything else folds: what we asked, what they
// said, the conversation and {AM}'s note, each folded every time it opens,
// so a busy conversation never follows the merchant from question to question.
//
// A send completes the question when it leaves the queue on the next read;
// the host then offers the next one (sheetFlow.ts).

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { CheckIcon, PaperclipIcon, PencilLineIcon, PhoneIcon, RefreshCwIcon, SendIcon } from "lucide-react";
import { decideMany, postMessage, requestCall, type ClientPagePayload, type DecisionBatchInput, type Thread, type ThreadMessage } from "@service/aeo/clientReview";
import {
    DrawerBlock,
    DrawerFold,
    DrawerFoot,
    DrawerHead,
    DrawerQuote,
    FOCUS_RING,
    LATE_TEXT,
    PillButton,
    SURFACE,
    TYPE,
    useDrawerFocusClaim,
    type DrawerStatus,
} from "@oura/ui";
import { docDate, trimText } from "@oura/docs";
import { useViewAs } from "@hook/use-view-as";
import { useWritesPaused } from "@context/maintenance";
import { cn } from "../../lib/cn";
import { dayDate } from "../../lib/oura";
import { ItemThread } from "../../components/ItemThread";
import { hasUnsentReply, keepUnsentReply, readUnsentReply } from "../../components/unsentReplies";
import { ContradictionEvidenceBlock, contradictionEvidence } from "../../components/ContradictionEvidence";
import { AnswerInput } from "../AnswerCard";
import { PackNoteBody } from "../PackNote";
import {
    ASK_GROUP,
    TAKES_BACK,
    accountManager,
    askFact,
    askGroup,
    draftDecision,
    isOpen,
    needsYou,
    noteBlocks,
    plural,
    publicFacingLine,
    type AnswerDraft,
    type Ask,
} from "../asks";
import type { SheetFlow } from "./sheetFlow";

/** A control an answer is made with: where focus goes back after a failed send, or after Answer now. */
const ANSWER_CONTROL = 'button:not([tabindex="-1"]), input, textarea';

const FIELD = cn(
    TYPE.tb,
    "block w-full resize-y rounded-xl border-0 bg-gray-50 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600",
);

type Stage = "open" | "replied" | "later" | "revising" | "answered";

function stageOf(ask: Ask): Stage {
    const { item } = ask;
    if (isOpen(item)) return "open";
    if (needsYou(item)) return "replied";
    if (item.decision === "deferred") return "later";
    if (item.decision === "changes_requested") return "revising";
    return "answered";
}

/** Messages people wrote (not the system's own lines). */
const spoken = (m: ThreadMessage) => m.kind === "message" || m.kind === "suggested_edit";

export interface QuestionSheetProps {
    ask: Ask;
    thread: Thread | null;
    payload: ClientPagePayload;
    draft: AnswerDraft | undefined;
    setDraft: (itemId: string, draft: AnswerDraft | null) => void;
    onRefresh: () => Promise<void>;
    flow: SheetFlow;
}

export function QuestionSheet({ ask, thread, payload, draft, setDraft, onRefresh, flow }: QuestionSheetProps) {
    const { item, pack } = ask;
    const who = accountManager(payload);
    const viewer = payload.viewer.name;
    const businessId = payload.business.id;
    const stage = stageOf(ask);
    const group = ASK_GROUP[askGroup(item)];
    const claim = useDrawerFocusClaim();
    const answerRef = useRef<HTMLDivElement>(null);
    const replyRef = useRef<HTMLTextAreaElement>(null);
    const errorId = useId();
    // Staff viewing as the merchant read; they do not answer (#4112).
    const { active: readOnly } = useViewAs();
    const writesPaused = useWritesPaused();

    const [sending, setSending] = useState(false);
    const [deferring, setDeferring] = useState(false);
    const [reopening, setReopening] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reply, setReply] = useState(() => readUnsentReply("client", item.id)?.body ?? "");
    /** Set by Answer now and Change my answer: focus the answer once the question is open again. */
    const focusAnswer = useRef(false);

    const busy = sending || deferring || reopening;
    const { setBusy } = flow;
    useEffect(() => {
        setBusy(busy);
    }, [busy, setBusy]);
    useEffect(() => () => setBusy(false), [setBusy]);

    // What they typed in the reply box stays with the question, as the thread's own box does.
    useEffect(() => {
        keepUnsentReply("client", item.id, { body: reply, file: readUnsentReply("client", item.id)?.file ?? null });
    }, [item.id, reply]);

    // Reopened here (Answer now, Change my answer): the answer box is back, and takes focus.
    useEffect(() => {
        if (stage !== "open" || !focusAnswer.current) return;
        focusAnswer.current = false;
        answerRef.current?.querySelector<HTMLElement>(`fieldset :is(${ANSWER_CONTROL})`)?.focus();
    }, [stage]);

    const decision = stage === "open" ? draftDecision(item, draft) : null;
    const contradiction = contradictionEvidence(item);
    const messages = thread?.messages ?? [];
    const said = messages.filter(spoken);
    const lastStaff = [...said].reverse().find((m) => m.authorKind === "staff") ?? null;
    // Our words since their last: what a reply answers.
    const lastMine = said.map((m) => m.authorKind).lastIndexOf("client");
    const sinceTheirs = said.slice(lastMine + 1).filter((m) => m.authorKind === "staff");
    const workStatus = payload.decisions.find((d) => d.id === item.id)?.workStatus?.trim() || null;
    // "From {AM}" quotes their latest message above the help fold; the thread inside it leaves that one out.
    const quotedAbove: ReadonlySet<string> = stage === "open" && lastStaff ? new Set([lastStaff.id]) : new Set();
    const hasNote = noteBlocks(pack).length > 0;

    const fact = askFact(ask, thread);
    const clock =
        stage === "later"
            ? dayDate(pack.publishedAt) && { text: `Asked ${dayDate(pack.publishedAt)}` }
            : fact && { text: fact.text, late: fact.late };
    const status: DrawerStatus = { tone: group.tone, label: group.label, clock: clock || null };

    const focusField = () => {
        if (stage === "replied") replyRef.current?.focus();
        else answerRef.current?.querySelector<HTMLElement>(`fieldset :is(${ANSWER_CONTROL})`)?.focus();
    };
    const fail = (message: string) => {
        setError(message);
        requestAnimationFrame(focusField);
    };
    /**
     * A decision, sent: the failure to show, or null when the server took it.
     * Only the write is caught here, so a page re-read after it can never be
     * reported as the write failing (and offer to send it twice).
     */
    const decide = async (d: DecisionBatchInput, failText: string): Promise<string | null> => {
        try {
            const result = await decideMany([d], viewer);
            return result.failed.length > 0 ? result.failed[0].message || failText : null;
        } catch {
            return failText;
        }
    };
    /** After a write the server took: read the page again. A failed re-read keeps the page on screen (the workspace keeps the last good one). */
    const settle = async () => {
        await onRefresh().catch(() => undefined);
    };

    const send = async (override?: DecisionBatchInput | null) => {
        const d = override ?? decision;
        if (!d || busy || writesPaused) return;
        setSending(true);
        setError(null);
        const problem = await decide(d, "Your answer did not send. It is still here. Try again.");
        if (problem) {
            fail(problem);
            setSending(false);
            return;
        }
        setDraft(item.id, null);
        await settle();
        flow.said(`Sent to ${who}.`);
        setSending(false);
    };

    const defer = async () => {
        if (busy) return;
        setDeferring(true);
        setError(null);
        const problem = await decide({ itemId: item.id, decision: "deferred" }, "That did not go through. Try again.");
        if (problem) {
            fail(problem);
            setDeferring(false);
            return;
        }
        await settle();
        flow.said("Moved to Later.");
        setDeferring(false);
    };

    const sendReply = async () => {
        const body = reply.trim();
        if (!body || !thread || busy || writesPaused) return;
        setSending(true);
        setError(null);
        try {
            await postMessage(thread.id, { authorKind: "client", authorName: viewer, body });
        } catch {
            fail("Your reply did not send. It is still here. Try again.");
            setSending(false);
            return;
        }
        setReply("");
        await settle();
        flow.said(`Sent to ${who}.`);
        setSending(false);
    };

    /** Answer now and Change my answer: back to not answered, answered here. */
    const reopen = async () => {
        if (busy) return;
        setReopening(true);
        setError(null);
        const problem = await decide({ itemId: item.id, decision: "pending" }, "That question did not reopen. Try again.");
        if (problem) {
            setConfirming(false);
            setError(problem);
            setReopening(false);
            return;
        }
        focusAnswer.current = true;
        await settle();
        setConfirming(false);
        setReopening(false);
    };

    const onReplyKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void sendReply();
        }
    };

    const errorLine = error && (
        <p id={errorId} role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-2")}>
            {error}
        </p>
    );
    const message = flow.message ? { tone: "success" as const, text: flow.message } : null;

    // The folds every settled question keeps: what they said (an agreement too, as "You agreed"),
    // what we asked, the conversation, the note. Open, so the whole answer and its file show.
    const youSaid = stage !== "open" && stage !== "later" && (
        <DrawerFold id="merchant-question.said" label={item.decision === "approved" ? "You agreed" : "You said"} note={youSaidNote(ask)} defaultOpen>
            <YouSaid ask={ask} />
        </DrawerFold>
    );
    const weAsked = stage !== "open" && (
        <DrawerFold id="merchant-question.asked" label="We asked">
            <WeAsked ask={ask} />
        </DrawerFold>
    );
    const conversation = stage !== "open" && messages.length > 0 && (
        <DrawerFold id="merchant-question.thread" label={`Conversation · ${messages.length}`}>
            <ThreadLines messages={messages} />
        </DrawerFold>
    );
    const note = hasNote && (
        <DrawerFold id="merchant-question.note" label={`${who}'s note`} note={pack.title}>
            <PackNoteBody pack={pack} />
        </DrawerFold>
    );

    let body: ReactNode;
    let foot: ReactNode;
    switch (stage) {
        case "open":
            body = (
                <>
                    <DrawerBlock label="Your answer" busy={sending}>
                        <div ref={answerRef}>
                            {item.why && <p className="m-0 text-gray-700">{item.why}</p>}
                            {contradiction && <ContradictionEvidenceBlock evidence={contradiction} className="mt-3" />}
                            <AnswerInput
                                item={item}
                                draft={draft}
                                onDraft={(d) => {
                                    setDraft(item.id, d);
                                    if (error) setError(null);
                                }}
                                businessId={businessId}
                                disabled={sending || readOnly}
                                claim={claim}
                                onSubmit={() => void send()}
                                // Agree is the answer: it sends straight away. Change opens the box.
                                onAgree={() => {
                                    const agreed: AnswerDraft = { pick: "agree" };
                                    setDraft(item.id, agreed);
                                    void send(draftDecision(item, agreed));
                                }}
                            />
                            {errorLine}
                            {item.holdsUp && <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>Holds up {item.holdsUp}.</p>}
                            {publicFacingLine(`${item.title} ${item.why ?? ""}`, who) && (
                                <p className={cn(TYPE.tm, "m-0 mt-1 text-gray-500")}>{publicFacingLine(`${item.title} ${item.why ?? ""}`, who)}</p>
                            )}
                        </div>
                    </DrawerBlock>
                    {lastStaff && (
                        <DrawerBlock label={`From ${lastStaff.authorName || who}`}>
                            <DrawerQuote who="" at={lastStaff.at}>
                                {lastStaff.body}
                            </DrawerQuote>
                        </DrawerBlock>
                    )}
                    {note}
                    <DrawerFold
                        id="merchant-question.help"
                        label={`Ask ${who} or book a call`}
                        note={said.length > quotedAbove.size ? plural(said.length - quotedAbove.size, "message") : undefined}
                        defaultOpen={hasUnsentReply("client", item.id)}
                    >
                        {/* The thread brings its own padding and rule: it runs to the block's edges. */}
                        <div className="-mx-4 -mt-1">
                            <ItemThread
                                compact
                                skipIds={quotedAbove}
                                businessId={businessId}
                                item={item}
                                thread={thread}
                                me="client"
                                myName={viewer}
                                sendLabel="Send message"
                                sendNote="This does not answer the question."
                                onPost={async (input) => {
                                    if (!thread) return;
                                    await postMessage(thread.id, { authorKind: "client", authorName: viewer, ...input });
                                    await settle();
                                }}
                                onRequestCall={async (preference, callNote) => {
                                    await requestCall(businessId, { itemId: item.id, requestedBy: viewer, preference, note: callNote });
                                    await settle();
                                }}
                            />
                        </div>
                    </DrawerFold>
                </>
            );
            foot = (
                <DrawerFoot
                    done={flow.done}
                    message={message}
                    first={
                        <PillButton writes variant="primary" icon={SendIcon} busy={sending} disabled={!decision || deferring || readOnly} onClick={() => void send()}>
                            Send answer
                        </PillButton>
                    }
                    second={
                        <PillButton writes busy={deferring} disabled={sending || readOnly} onClick={() => void defer()}>
                            Not now
                        </PillButton>
                    }
                />
            );
            break;
        case "replied":
            body = (
                <>
                    <DrawerBlock label={`Reply to ${lastStaff?.authorName || who}`} busy={sending}>
                        <Quotes messages={sinceTheirs.length > 0 ? sinceTheirs : lastStaff ? [lastStaff] : []} />
                        <label htmlFor={`${errorId}-reply`} className="sr-only">
                            Your reply
                        </label>
                        <textarea
                            id={`${errorId}-reply`}
                            ref={(el) => {
                                replyRef.current = el;
                                const release = claim(el);
                                return () => {
                                    replyRef.current = null;
                                    if (typeof release === "function") release();
                                };
                            }}
                            rows={3}
                            value={reply}
                            onChange={(e) => {
                                setReply(e.target.value);
                                if (error) setError(null);
                            }}
                            onKeyDown={onReplyKey}
                            aria-describedby={error ? errorId : undefined}
                            placeholder={`Write to ${who}`}
                            className={cn(FIELD, sinceTheirs.length > 0 || lastStaff ? "mt-3" : "mt-1")}
                        />
                        {errorLine}
                    </DrawerBlock>
                    {youSaid}
                    {weAsked}
                    {conversation}
                    {note}
                </>
            );
            foot = (
                <DrawerFoot
                    done={flow.done}
                    message={message}
                    first={
                        <PillButton writes variant="primary" icon={SendIcon} busy={sending} disabled={!reply.trim() || !thread || readOnly} onClick={() => void sendReply()}>
                            Send reply
                        </PillButton>
                    }
                />
            );
            break;
        case "later":
            body = (
                <>
                    {error && (
                        <DrawerBlock>
                            <p role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0")}>
                                {error}
                            </p>
                        </DrawerBlock>
                    )}
                    {weAsked}
                    {conversation}
                    {note}
                </>
            );
            foot = (
                <DrawerFoot
                    done={flow.done}
                    message={message}
                    first={
                        <PillButton writes variant="primary" busy={reopening} disabled={readOnly} onClick={() => void reopen()}>
                            Answer now
                        </PillButton>
                    }
                />
            );
            break;
        default: {
            const takesBack = item.decision === "pending" || item.decision === "deferred" ? null : TAKES_BACK[item.decision];
            body = (
                <>
                    <DrawerBlock label="What happens now">
                        <p className="m-0">{whatHappensNow(stage, who, workStatus)}</p>
                        {error && (
                            <p role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-2")}>
                                {error}
                            </p>
                        )}
                    </DrawerBlock>
                    {youSaid}
                    {weAsked}
                    {conversation}
                    {note}
                </>
            );
            foot = (
                <DrawerFoot
                    done={flow.done}
                    message={message}
                    confirm={
                        confirming && takesBack
                            ? {
                                text: `${takesBack} ${who} pauses the work until you answer again.`,
                                verb: "Change",
                                busy: reopening,
                                onConfirm: () => void reopen(),
                                onCancel: () => setConfirming(false),
                            }
                            : null
                    }
                    second={takesBack ? <PillButton writes disabled={readOnly} onClick={() => setConfirming(true)}>Change my answer</PillButton> : undefined}
                />
            );
        }
    }

    return (
        <>
            <DrawerHead title={item.title} status={status} />
            {body}
            {foot}
        </>
    );
}

/**
 * Up to 3 of the other side's messages, newest last; "Show 2 earlier" for
 * the rest. The newest carries no date: the status line says when it came.
 */
function Quotes({ messages }: { messages: ThreadMessage[] }) {
    const [all, setAll] = useState(false);
    if (messages.length === 0) return null;
    const hidden = all ? 0 : Math.max(0, messages.length - 3);
    const shown = messages.slice(hidden);
    return (
        <div className="space-y-2">
            {hidden > 0 && (
                <button
                    type="button"
                    onClick={() => setAll(true)}
                    className={cn(TYPE.tc, "relative rounded font-medium text-blue-600 hover:text-blue-700 after:absolute after:-inset-x-1 after:-inset-y-[13px] after:content-['']", FOCUS_RING)}
                >
                    Show {hidden} earlier
                </button>
            )}
            {shown.map((m, i) => (
                <DrawerQuote key={m.id} who="" at={i === shown.length - 1 ? null : m.at}>
                    {m.body || (m.fileName ? `Sent a file: ${m.fileName}` : "")}
                </DrawerQuote>
            ))}
        </div>
    );
}

/**
 * "What happens now" as a sentence, never a bare status word: who has it,
 * then where the work stands.
 */
function whatHappensNow(stage: Stage, who: string, workStatus: string | null): string {
    if (stage === "revising") return `${who} is making the change you asked for.`;
    const s = workStatus?.toLowerCase() ?? "";
    if (/in progress|under way|underway/.test(s)) return `${who} has it. The work is in progress.`;
    if (/not started/.test(s)) return `${who} has it. The work has not started yet.`;
    if (/done|complete|live|published/.test(s)) return `${who} has it. The work is done.`;
    return workStatus ? `${who} has it. ${workStatus.replace(/[.]?$/, ".")}` : `${who} has it.`;
}

/** What they answered, cut to 40 characters, for the folded "You said". */
function youSaidNote({ item }: Ask): string | null {
    switch (item.decision) {
        case "approved":
            return docDate(item.decidedAt) ? `Agreed ${docDate(item.decidedAt)}` : "Agreed";
        case "answered":
            // The file's name too, so an answer with a photo never reads as words alone.
            return [item.answer ? trimText(item.answer, 40) : null, item.fileName ? trimText(item.fileName, 30) : null].filter(Boolean).join(" · ") || "Answered";
        case "changes_requested":
            return "You asked for a change";
        default:
            return null;
    }
}

/** The merchant's answer, in their terms. The words of a change request are in the conversation. */
function YouSaid({ ask: { item } }: { ask: Ask }) {
    switch (item.decision) {
        case "approved":
            return item.proposed ? (
                <>
                    Agreed:
                    <span className="mt-0.5 block whitespace-pre-line text-gray-600">{item.proposed}</span>
                </>
            ) : (
                <>Agreed.</>
            );
        case "answered": {
            // Words, a file, or both: a price list typed out, or its photo.
            const file = item.fileName ? (
                item.fileUrl ? (
                    <a
                        href={item.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={cn("inline-flex items-center gap-1.5 rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}
                    >
                        <PaperclipIcon aria-hidden size={14} />
                        {item.fileName}
                    </a>
                ) : (
                    <span className="inline-flex items-center gap-1.5">
                        <PaperclipIcon aria-hidden size={14} className="text-gray-400" />
                        {item.fileName}
                    </span>
                )
            ) : null;
            if (!item.answer) return file ?? <>Answered.</>;
            return (
                <>
                    <span className="whitespace-pre-line">{item.answer}</span>
                    {file && <span className="mt-1.5 block">{file}</span>}
                </>
            );
        }
        case "changes_requested":
            return <>You asked for a change. What you wrote is in the conversation.</>;
        default:
            return <>Not answered yet.</>;
    }
}

/** What we asked: why, what we proposed or the outline, and an assistant's words when that is what it is about. */
function WeAsked({ ask: { item } }: { ask: Ask }) {
    const contradiction = contradictionEvidence(item);
    return (
        <>
            <p className="m-0 text-gray-700">{item.why || item.title}</p>
            {item.proposed && (
                <div className={cn(SURFACE.inner, "mt-2")}>
                    <p className={cn(TYPE.lab, "m-0")}>{item.decision === "approved" ? "You agreed to this" : "We proposed"}</p>
                    <p className="m-0 mt-0.5 whitespace-pre-line text-gray-900">{item.proposed}</p>
                </div>
            )}
            {!item.proposed && item.outline && item.outline.length > 0 && (
                <ul className="list-plain m-0 mt-2 space-y-1.5 p-0 text-gray-700">
                    {item.outline.map((line) => (
                        <li key={line} className="flex gap-2.5">
                            <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
                            {line}
                        </li>
                    ))}
                </ul>
            )}
            {contradiction && <ContradictionEvidenceBlock evidence={contradiction} className="mt-3" />}
        </>
    );
}

const SYSTEM_ICON = { version: RefreshCwIcon, call_request: PhoneIcon, suggested_edit: PencilLineIcon, decision: CheckIcon, message: CheckIcon } as const;

/** The conversation, read only: who wrote, when, and what; the system's own lines quieter. */
function ThreadLines({ messages }: { messages: ThreadMessage[] }) {
    return (
        <ol className="list-plain m-0 p-0">
            {messages.map((m) => {
                const when = dayDate(m.at);
                if (!spoken(m)) {
                    const Icon = SYSTEM_ICON[m.kind] ?? CheckIcon;
                    return (
                        <li key={m.id} className={cn(TYPE.tm, "flex items-start gap-2 border-t border-gray-100 py-2 text-gray-500 first:border-t-0 first:pt-0")}>
                            <Icon aria-hidden size={12} className="mt-0.5 shrink-0 text-gray-400" />
                            <span className="min-w-0 flex-1 break-words">{m.body}</span>
                            {when && <span className="shrink-0 tabular-nums">{when}</span>}
                        </li>
                    );
                }
                const name = m.authorKind === "client" ? m.authorName || "You" : m.authorName || "ourteam";
                return (
                    <li key={m.id} className="border-t border-gray-100 py-2.5 first:border-t-0 first:pt-0">
                        <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                            <span className="font-medium text-gray-900">{name}</span>
                            {when && <span className="tabular-nums"> · {when}</span>}
                        </p>
                        {m.body && <p className="m-0 mt-0.5 whitespace-pre-line break-words text-gray-700">{m.body}</p>}
                        {m.suggestedEdit && (
                            <p className={cn(SURFACE.inner, "m-0 mt-1.5 whitespace-pre-line break-words text-gray-900")}>{m.suggestedEdit.after}</p>
                        )}
                        {m.fileName && (
                            <p className={cn(TYPE.tc, "m-0 mt-1 inline-flex items-center gap-1.5 text-gray-600")}>
                                <PaperclipIcon aria-hidden size={12} className="text-gray-400" />
                                {m.fileName}
                            </p>
                        )}
                    </li>
                );
            })}
        </ol>
    );
}
